import React from 'react';
import { TEXT, TEXT2, TEXT3, BLUE, SUCCESS, WARNING, DANGER, BORDER, FONT } from '../../shared';

function Sparkline({ data = [], color = BLUE, height = 28 }) {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const y = height - 3 - ((v - min) / range) * (height - 6);
    return `${x},${y}`;
  }).join(' ');
  const fillPts = `0,${height} ${pts} 100,${height}`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={fillPts} fill={`url(#sg-${color.replace('#','')})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MetricCard({ label, value, change, trend, color, sparkData }) {
  const isPositive = trend === 'up';
  const isNeutral = trend === 'neutral' || !trend;
  const changeColor = isNeutral ? TEXT3 : isPositive ? SUCCESS : DANGER;
  const arrow = isNeutral ? null : isPositive ? (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 2L9 7H1L5 2Z" fill={SUCCESS} />
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 8L1 3H9L5 8Z" fill={DANGER} />
    </svg>
  );

  return (
    <div style={{
      flex: '1 1 130px',
      minWidth: 0,
      background: 'rgba(255,255,255,0.72)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      border: '1px solid rgba(255,255,255,0.65)',
      borderRadius: 14,
      padding: '14px 16px 10px',
      boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 6px 20px -10px rgba(11,107,211,0.14)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 4 }}>
        {value}
      </div>
      {change && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 6 }}>
          {arrow}
          <span style={{ fontSize: 11, fontWeight: 600, color: changeColor }}>{change}</span>
        </div>
      )}
      {sparkData?.length > 1 && (
        <div style={{ marginTop: 4, opacity: 0.9 }}>
          <Sparkline data={sparkData} color={color} height={28} />
        </div>
      )}
    </div>
  );
}

export default function AnalyticsWidget({ data, onAction }) {
  if (!data) return null;

  const metrics = [
    {
      label: 'Impressions',
      value: data.impressions != null ? Number(data.impressions).toLocaleString() : '—',
      change: data.impressions_change,
      trend: data.impressions_trend,
      color: BLUE,
      sparkData: data.impressions_spark || [],
    },
    {
      label: 'Reach',
      value: data.reach != null ? Number(data.reach).toLocaleString() : '—',
      change: data.reach_change,
      trend: data.reach_trend,
      color: '#0D9488',
      sparkData: data.reach_spark || [],
    },
    {
      label: 'Engagement',
      value: data.engagement != null ? (typeof data.engagement === 'number' && data.engagement < 1 ? `${(data.engagement * 100).toFixed(1)}%` : data.engagement.toLocaleString()) : '—',
      change: data.engagement_change,
      trend: data.engagement_trend,
      color: '#9333EA',
      sparkData: data.engagement_spark || [],
    },
    {
      label: 'Followers',
      value: data.followers != null ? Number(data.followers).toLocaleString() : '—',
      change: data.followers_change,
      trend: data.followers_trend,
      color: SUCCESS,
      sparkData: data.followers_spark || [],
    },
  ].filter(m => m.value !== '—');

  return (
    <div style={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(22px)',
      WebkitBackdropFilter: 'blur(22px)',
      border: '1px solid rgba(255,255,255,0.60)',
      borderRadius: 18,
      padding: '18px 18px 14px',
      boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 10px 28px -12px rgba(11,107,211,0.16)',
      fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 700, color: TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Analytics{data.period ? ` · ${data.period}` : ''}
          </span>
        </div>
        {data.platform && (
          <span style={{ fontSize: 11, fontWeight: 600, color: TEXT3, background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 999, padding: '2px 8px' }}>
            {data.platform}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {metrics.map((m, i) => (
          <MetricCard key={i} {...m} />
        ))}
      </div>
    </div>
  );
}
